import type {
  LeagueTeamPlayers,
  PlayerRequestData,
  PlayersQueryParams,
  PlayersResponse
} from '@library/types/player';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { getNormalizedPlayers } from '@library/utils/players';

const { admin } = apiPaths;

export const playersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTeamPlayers: build.query<
      LeagueTeamPlayers,
      { leagueId: string; gameDate: string; teams: string }
    >({
      query: ({ leagueId, gameDate, teams }) => ({
        url: `${admin}rosters`,
        method: 'GET',
        params: {
          league_id: leagueId,
          date: gameDate,
          teams
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: ['Players'],
      transformResponse: (response: LeagueTeamPlayers) =>
        getNormalizedPlayers(response)
    }),
    uploadPlayersByCSV: build.mutation<unknown, FormData>({
      query: (csv) => ({
        url: `${admin}upload_players`,
        method: 'POST',
        body: csv,
        headers: {
          Accept: 'multipart/form-data'
        }
      }),
      transformErrorResponse: (response) => response.data
    }),
    getPlayers: build.query<PlayersResponse, PlayersQueryParams>({
      query: ({
        fromDate,
        leagueId,
        page,
        perPage,
        searchValue,
        sortBy,
        sortDir,
        toDate
      }) => ({
        url: `${admin}models/players`,
        method: 'GET',
        params: {
          page,
          per_page: perPage,
          'q[league_id_eq]': leagueId,
          'q[name_or_team_name_cont]': searchValue,
          'q[created_at_gteq]': `${fromDate}T00:00:00`,
          'q[created_at_lteq]': `${toDate}T23:59:59`,
          sort_by: sortBy,
          sort_dir: sortDir
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    }),
    createPlayer: build.mutation<PlayersResponse, PlayerRequestData>({
      query: (newPlayerData) => ({
        url: `${admin}models/players`,
        method: 'POST',
        body: newPlayerData,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data
    }),
    updatePlayer: build.mutation<
      PlayersResponse,
      { id: string; data: PlayerRequestData }
    >({
      query: ({ id, data }) => ({
        url: `${admin}models/players/${id}`,
        method: 'PATCH',
        body: data,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data
    }),
    deletePlayer: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `${admin}models/players/${id}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformErrorResponse: (response) => response.data
    })
  })
});

export const {
  useGetTeamPlayersQuery,
  useUploadPlayersByCSVMutation,
  useGetPlayersQuery,
  useCreatePlayerMutation,
  useUpdatePlayerMutation,
  useDeletePlayerMutation
} = playersApi;
